/**
 * cn —— 条件类名合并工具
 *
 * 【这个工具解决什么问题？】
 * 在使用 Tailwind CSS 时，我们经常需要根据条件组合不同的 CSS 类名。
 * 比如：一个按钮在激活时有 'bg-blue-500'，否则是 'bg-gray-300'。
 *
 * cn() 函数可以安全地合并类名，并自动处理冲突：
 * - clsx：负责条件拼接（根据 true/false 决定是否包含某个类名）
 * - tailwind-merge：负责解决 Tailwind 类名冲突（后面的覆盖前面的）
 *
 * 【为什么需要 tailwind-merge？】
 * Tailwind 的类名是原子化的，比如 'px-2' 和 'px-4' 都设置 padding。
 * 如果同时出现，CSS 优先级取决于文件中的顺序，而不是类名在 HTML 中的顺序。
 * twMerge 会智能地保留最后一个，移除前面的冲突类名。
 *
 * 【常见用法】
 * ```ts
 * // 基础拼接
 * cn('px-2', 'py-1')              // 'px-2 py-1'
 *
 * // 冲突解决（px-4 覆盖 px-2）
 * cn('px-2', 'px-4')              // 'px-4'
 *
 * // 条件类名（对象写法）
 * cn('base-class', { active: isActive, disabled: isDisabled })
 *
 * // 条件类名（数组写法）
 * cn(['flex', condition && 'hidden'])
 *
 * // 混合使用
 * cn(
 *   'rounded-lg px-4 py-2',           // 基础样式
 *   isActive && 'bg-blue-500',         // 条件样式
 *   { 'opacity-50': isDisabled }       // 条件样式（对象形式）
 * )
 * ```
 */

import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * 合并 CSS 类名
 *
 * 【参数类型 ClassValue】
 * ClassValue 是 clsx 库定义的类型，支持以下几种形式：
 * - 字符串：'px-2'
 * - 对象：{ active: true, disabled: false }
 * - 数组：['px-2', condition && 'hidden']
 * - falsy 值（false/null/undefined）：会被自动忽略
 *
 * 【twMerge(clsx(...)) 的工作流程】
 * 1. clsx() 先将所有输入拼接成一个字符串
 * 2. twMerge() 再处理 Tailwind 类名冲突
 *
 * @param inputs - 任意数量的类名（字符串/对象/数组/条件表达式）
 * @returns 合并后的类名字符串
 *
 * @example
 * cn('px-2', 'py-1')                         // 'px-2 py-1'
 * cn('px-2', 'px-4')                         // 'px-4'（px-4 覆盖了 px-2）
 * cn('text-red-500', 'text-blue-500')        // 'text-blue-500'
 * cn({ 'active': true, 'hidden': false })    // 'active'（false 的被忽略）
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
